import {
  isNormalizedModifier,
  normalizePttCode,
} from "@bezoned/shared/features/push-to-talk/push-to-talk.utils.js";
import { useEffect, useRef } from "react";

import { usePushToTalkStore } from "./push-to-talk.store.js";

interface UsePushToTalkButtonOptions {
  isEnabled?: boolean;
}

const MOUSE_PREFIX = "Mouse";

export const usePushToTalkButton = (
  onChange: (active: boolean) => void,
  options: UsePushToTalkButtonOptions = {}
) => {
  const isKeyDownRef = useRef(false);
  const { isEnabled = true } = options;

  const code = usePushToTalkStore((state) => state.code);

  useEffect(() => {
    const isMouseCode = code.startsWith(MOUSE_PREFIX);
    const mouseButton = isMouseCode
      ? Number.parseInt(code.slice(MOUSE_PREFIX.length), 10)
      : null;

    const matchesKeyboardEvent = (event: KeyboardEvent): boolean => {
      if (isMouseCode) {
        return false;
      }
      if (isNormalizedModifier(code)) {
        return event.code.startsWith(code);
      }
      return normalizePttCode(event.code) === code;
    };

    const matchesMouseEvent = (event: MouseEvent): boolean => {
      if (!isMouseCode || mouseButton === null) {
        return false;
      }
      return event.button === mouseButton;
    };

    const isInputActive = () => {
      const { activeElement } = document;
      return (
        activeElement instanceof HTMLElement &&
        (["INPUT", "TEXTAREA"].includes(activeElement.tagName) ||
          activeElement.getAttribute("role") === "textbox")
      );
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isKeyDownRef.current || !isEnabled) {
        return;
      }
      if (!matchesKeyboardEvent(event)) {
        return;
      }
      if (isInputActive()) {
        return;
      }

      event.preventDefault();
      isKeyDownRef.current = true;
      onChange(true);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!(isKeyDownRef.current && isEnabled)) {
        return;
      }
      if (!matchesKeyboardEvent(event)) {
        return;
      }

      event.preventDefault();
      isKeyDownRef.current = false;
      onChange(false);
    };

    const handleMouseDown = (event: MouseEvent) => {
      if (isKeyDownRef.current || !isEnabled) {
        return;
      }
      if (!matchesMouseEvent(event)) {
        return;
      }
      if (isInputActive()) {
        return;
      }

      event.preventDefault();
      isKeyDownRef.current = true;
      onChange(true);
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (!(isKeyDownRef.current && isEnabled)) {
        return;
      }
      if (!matchesMouseEvent(event)) {
        return;
      }

      event.preventDefault();
      isKeyDownRef.current = false;
      onChange(false);
    };

    const handleVisibilityChange = () => {
      if (document.hidden && isKeyDownRef.current) {
        isKeyDownRef.current = false;
        onChange(false);
      }
    };

    const handleBlur = () => {
      if (isKeyDownRef.current) {
        isKeyDownRef.current = false;
        onChange(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);

      if (isKeyDownRef.current) {
        isKeyDownRef.current = false;
        onChange(false);
      }
    };
  }, [onChange, code, isEnabled]);

  useEffect(() => {
    if (!isEnabled && isKeyDownRef.current) {
      isKeyDownRef.current = false;
      onChange(false);
    }
  }, [isEnabled, onChange]);
};
